import { ApiError, getJSON, postJSON } from '../../lib/api';

/** Mirrors the public contract of the /api/v1/auth endpoints. */
export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
  /** Set for people of a client (ADR-027): they see the client portal. */
  client_id?: string | null;
  /** A super administrator inside a tenant entered from the platform (ADR-028). */
  entered_from_platform?: boolean;
}

export interface SessionTenant {
  id: string;
  slug: string;
  name: string;
  /** IANA time zone for rendering timestamps. */
  timezone: string;
  /** The tenant of the super administrators (ADR-028). */
  platform?: boolean;
}

export interface SessionResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  user: SessionUser;
  tenant: SessionTenant;
  /** Effective permissions, resolved server-side. UI gating only; the API enforces them. */
  permissions: string[];
}

export interface LoginCredentials {
  tenant: string;
  email: string;
  password: string;
}

const BASE = '/api/v1/auth';

export function login(creds: LoginCredentials): Promise<SessionResponse> {
  return postJSON<SessionResponse>(`${BASE}/login`, creds);
}

/** Enters a tenant from the platform; the refresh cookie now holds that session (ADR-028). */
export function enterTenant(token: string, tenantId: string): Promise<SessionResponse> {
  return postJSON<SessionResponse>(`${BASE}/enter`, { tenant_id: tenantId }, { token });
}

/** Returns from an entered tenant to the platform session. */
export function leaveTenant(token: string): Promise<SessionResponse> {
  return postJSON<SessionResponse>(`${BASE}/leave`, {}, { token });
}

/** An invitation as shown to the invited person (ADR-030). */
export interface Invitation {
  tenant_name: string;
  tenant_slug: string;
  display_name: string;
  email: string;
  expires_at?: string;
}

export function inspectInvite(token: string): Promise<Invitation> {
  return postJSON<Invitation>(`${BASE}/invite`, { token });
}

/** E-mails a 6-digit confirmation code to the invited address. */
export function sendInviteCode(token: string): Promise<unknown> {
  return postJSON<unknown>(`${BASE}/invite/code`, { token });
}

export function acceptInvite(
  token: string,
  code: string,
  newPassword: string,
): Promise<Invitation> {
  return postJSON<Invitation>(`${BASE}/invite/accept`, { token, code, new_password: newPassword });
}

/** Asks for a password reset link by e-mail (ADR-029). Always 202, account or not. */
export function requestPasswordReset(tenant: string, email: string): Promise<unknown> {
  return postJSON<unknown>(`${BASE}/password-reset`, { tenant, email });
}

/** Sets a new password with the token of a reset link. */
export function confirmPasswordReset(token: string, newPassword: string): Promise<null> {
  return postJSON<null>(`${BASE}/password-reset/confirm`, { token, new_password: newPassword });
}

export function logout(token?: string): Promise<null> {
  return postJSON<null>(`${BASE}/logout`, {}, token ? { token } : {});
}

let inflight: Promise<SessionResponse> | null = null;

/**
 * Exchanges the HttpOnly refresh cookie for a new access token.
 *
 * Refresh tokens rotate on every use, so concurrent refreshes must not race:
 * calls are single-flighted within the tab and serialised across tabs with the
 * Web Locks API (where available). A 409 means another request rotated the
 * cookie moments ago; retrying once picks up the new cookie.
 */
export function refresh(): Promise<SessionResponse> {
  inflight ??= withCrossTabLock(refreshWithRetry).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function refreshWithRetry(): Promise<SessionResponse> {
  try {
    return await postJSON<SessionResponse>(`${BASE}/refresh`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      await new Promise((r) => setTimeout(r, 300));
      return postJSON<SessionResponse>(`${BASE}/refresh`);
    }
    throw err;
  }
}

function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request('vms-auth-refresh', fn);
}

export function changePassword(token: string, current: string, next: string): Promise<null> {
  return postJSON<null>(
    `${BASE}/password`,
    { current_password: current, new_password: next },
    { token },
  );
}

export function me(token: string): Promise<{ user: SessionUser; tenant: SessionTenant }> {
  return getJSON(`${BASE}/me`, { token });
}
